import { useState, useRef, useEffect } from 'react';
import {
  PatientNameDataFragment,
  ProgramTagWithCountDataFragment,
  ProgramTagDataFragment,
  usePatientNamesForRecipientsLazyQuery,
} from '../../../../generated/graphql';
import {
  sortPatientsByAlphabeticalAscending,
  PatientOrPatientNameData,
} from '../../../components/PatientItem';
import { convertPatientToPatientNameData } from '../helpers';
import { customToast } from '../../../components/ToastAlert/customToast';

export type TagPatientSelectionEntry = {
  patient: PatientNameDataFragment;
  selected: boolean;
};

export type TagSelectionEntry = {
  tag: ProgramTagWithCountDataFragment;
  patients: Record<string, TagPatientSelectionEntry>;
};

export type PatientSelectionEntry = {
  patient: PatientNameDataFragment;
  manual: boolean;
  selectedTagIds: string[];
};

export const getEnrollableRecipientPatients = (
  patientSelections: Record<string, PatientSelectionEntry>,
): PatientNameDataFragment[] =>
  Object.values(patientSelections)
    .filter((entry) => entry.manual || entry.selectedTagIds.length > 0)
    .map((entry) => entry.patient);

interface UseRecipientSelectionProps {
  watchPatientsOrTags: (
    | PatientNameDataFragment
    | ProgramTagDataFragment
    | ProgramTagWithCountDataFragment
  )[];
  providerProgramTags: ProgramTagWithCountDataFragment[];
  patientsList: PatientNameDataFragment[];
}

export const useRecipientSelection = ({
  watchPatientsOrTags,
  providerProgramTags,
  patientsList,
}: UseRecipientSelectionProps) => {
  const [tagSelections, setTagSelections] = useState<
    Record<string, TagSelectionEntry>
  >({});
  const [tagOrder, setTagOrder] = useState<string[]>([]);
  const [patientSelections, setPatientSelections] = useState<
    Record<string, PatientSelectionEntry>
  >({});
  const [patientOrder, setPatientOrder] = useState<string[]>([]);
  const selectionsInitializedRef = useRef(false);

  const [patientNamesForRecipientsQuery] =
    usePatientNamesForRecipientsLazyQuery();

  const normalizePatientName = (
    patient: PatientOrPatientNameData,
  ): PatientNameDataFragment =>
    patient.__typename === 'PatientName'
      ? patient
      : convertPatientToPatientNameData(patient);

  const sortAndNormalizePatients = (
    patients: PatientOrPatientNameData[] | undefined,
  ): PatientNameDataFragment[] => {
    const copy = patients ? [...patients] : [];
    return sortPatientsByAlphabeticalAscending(copy).map(normalizePatientName);
  };

  const isPatientName = (
    item:
      | PatientNameDataFragment
      | ProgramTagWithCountDataFragment
      | ProgramTagDataFragment,
  ): item is PatientNameDataFragment => item?.__typename === 'PatientName';

  const isProgramTag = (
    item:
      | PatientNameDataFragment
      | ProgramTagWithCountDataFragment
      | ProgramTagDataFragment,
  ): item is ProgramTagWithCountDataFragment =>
    item?.__typename === 'ProgramTag';

  const addPatientToState = (
    state: {
      tagSelections: Record<string, TagSelectionEntry>;
      tagOrder: string[];
      patientSelections: Record<string, PatientSelectionEntry>;
      patientOrder: string[];
    },
    patient: PatientNameDataFragment,
    options: { manual: boolean; tagId?: string },
  ) => {
    const patientId = patient.programInstanceId;
    const existing = state.patientSelections[patientId];
    const nextSelectedTagIds = new Set(existing?.selectedTagIds ?? []);

    if (options.tagId) {
      nextSelectedTagIds.add(options.tagId);
    }

    state.patientSelections[patientId] = {
      patient,
      manual: existing ? existing.manual || options.manual : options.manual,
      selectedTagIds: Array.from(nextSelectedTagIds),
    };

    if (!state.patientOrder.includes(patientId)) {
      state.patientOrder.push(patientId);
    }
  };

  const addTagToState = (
    state: {
      tagSelections: Record<string, TagSelectionEntry>;
      tagOrder: string[];
      patientSelections: Record<string, PatientSelectionEntry>;
      patientOrder: string[];
    },
    tag: ProgramTagWithCountDataFragment,
    patients: PatientNameDataFragment[],
  ) => {
    const existing = state.tagSelections[tag.id];
    const nextPatients: Record<string, TagPatientSelectionEntry> = existing
      ? { ...existing.patients }
      : {};

    patients.forEach((patient) => {
      nextPatients[patient.programInstanceId] = {
        patient,
        selected: true,
      };
      addPatientToState(state, patient, { manual: false, tagId: tag.id });
    });

    state.tagSelections[tag.id] = {
      tag,
      patients: nextPatients,
    };

    if (!state.tagOrder.includes(tag.id)) {
      state.tagOrder.push(tag.id);
    }
  };

  const removePatientFromState = (
    state: {
      tagSelections: Record<string, TagSelectionEntry>;
      tagOrder: string[];
      patientSelections: Record<string, PatientSelectionEntry>;
      patientOrder: string[];
    },
    patientId: string,
  ) => {
    const entry = state.patientSelections[patientId];
    if (!entry) {
      return;
    }

    // Mark patient as unselected across all tags
    Object.entries(state.tagSelections).forEach(([tagId, tagEntry]) => {
      const tagPatientEntry = tagEntry.patients[patientId];
      if (tagPatientEntry) {
        tagEntry.patients[patientId] = {
          ...tagPatientEntry,
          selected: false,
        };
      }
      const hasSelected = Object.values(tagEntry.patients).some(
        (patientEntry) => patientEntry.selected,
      );
      if (!hasSelected) {
        delete state.tagSelections[tagId];
        state.tagOrder = state.tagOrder.filter((id) => id !== tagId);
      }
    });

    delete state.patientSelections[patientId];
    state.patientOrder = state.patientOrder.filter((id) => id !== patientId);
  };

  const removeTagFromState = (
    state: {
      tagSelections: Record<string, TagSelectionEntry>;
      tagOrder: string[];
      patientSelections: Record<string, PatientSelectionEntry>;
      patientOrder: string[];
    },
    tagId: string,
  ) => {
    const tagEntry = state.tagSelections[tagId];
    if (!tagEntry) {
      return;
    }

    Object.entries(tagEntry.patients).forEach(
      ([patientId, tagPatientEntry]) => {
        if (!tagPatientEntry.selected) {
          return;
        }

        const patientEntry = state.patientSelections[patientId];
        if (!patientEntry) {
          return;
        }

        const remainingSelectedTagIds = patientEntry.selectedTagIds.filter(
          (id) => id !== tagId,
        );

        if (!patientEntry.manual && remainingSelectedTagIds.length === 0) {
          delete state.patientSelections[patientId];
          state.patientOrder = state.patientOrder.filter(
            (id) => id !== patientId,
          );
        } else {
          state.patientSelections[patientId] = {
            ...patientEntry,
            selectedTagIds: remainingSelectedTagIds,
          };
        }
      },
    );

    delete state.tagSelections[tagId];
    state.tagOrder = state.tagOrder.filter((id) => id !== tagId);
  };

  const cloneSelectionState = () => ({
    tagSelections: Object.fromEntries(
      Object.entries(tagSelections).map(([tagId, entry]) => [
        tagId,
        {
          tag: entry.tag,
          patients: Object.fromEntries(
            Object.entries(entry.patients).map(([patientId, patientEntry]) => [
              patientId,
              {
                patient: patientEntry.patient,
                selected: patientEntry.selected,
              },
            ]),
          ),
        },
      ]),
    ) as Record<string, TagSelectionEntry>,
    tagOrder: [...tagOrder],
    patientSelections: Object.fromEntries(
      Object.entries(patientSelections).map(([patientId, entry]) => [
        patientId,
        {
          patient: entry.patient,
          manual: entry.manual,
          selectedTagIds: [...entry.selectedTagIds],
        },
      ]),
    ) as Record<string, PatientSelectionEntry>,
    patientOrder: [...patientOrder],
  });

  const buildRecipientsValue = (
    tagSelectionsMap: Record<string, TagSelectionEntry>,
    tagOrderList: string[],
    patientSelectionsMap: Record<string, PatientSelectionEntry>,
    patientOrderList: string[],
  ) => {
    const tags = tagOrderList
      .map((tagId) => {
        const entry = tagSelectionsMap[tagId];
        if (!entry) {
          return null;
        }

        const selectedCount = Object.values(entry.patients).filter(
          (patientEntry) => patientEntry.selected,
        ).length;

        if (selectedCount === 0) {
          return null;
        }

        return {
          ...entry.tag,
          programCount: selectedCount,
        };
      })
      .filter(Boolean) as ProgramTagWithCountDataFragment[];

    const patients = patientOrderList
      .map((patientId) => patientSelectionsMap[patientId])
      .filter(
        (entry): entry is PatientSelectionEntry =>
          Boolean(entry) && entry.manual,
      )
      .map((entry) => entry.patient);

    return [...tags, ...patients];
  };

  useEffect(() => {
    if (selectionsInitializedRef.current) {
      return;
    }

    if (!watchPatientsOrTags.length) {
      return;
    }

    const initializeSelections = async () => {
      const nextState = {
        tagSelections: {} as Record<string, TagSelectionEntry>,
        tagOrder: [] as string[],
        patientSelections: {} as Record<string, PatientSelectionEntry>,
        patientOrder: [] as string[],
      };

      for (const item of watchPatientsOrTags) {
        if (isProgramTag(item)) {
          try {
            const { data } = await patientNamesForRecipientsQuery({
              variables: { programIds: [], programTagIds: [item.id] },
            });
            const fetchedPatients = sortAndNormalizePatients(
              data?.patientNamesForRecipients as
                | PatientOrPatientNameData[]
                | undefined,
            );
            addTagToState(
              nextState,
              item as ProgramTagWithCountDataFragment,
              fetchedPatients,
            );
          } catch (err) {
            customToast.error('Unable to load tag recipients.');
          }
        } else if (isPatientName(item)) {
          addPatientToState(nextState, item, { manual: true });
        }
      }

      selectionsInitializedRef.current = true;
      setTagSelections(nextState.tagSelections);
      setTagOrder(nextState.tagOrder);
      setPatientSelections(nextState.patientSelections);
      setPatientOrder(nextState.patientOrder);
    };

    void initializeSelections();
  }, [patientNamesForRecipientsQuery, watchPatientsOrTags]);

  const [patientOrTagQuery, setPatientOrTagQuery] = useState('');

  const watchedProgramInstanceIds = new Set(
    watchPatientsOrTags
      .map(
        (patientOrTag) =>
          patientOrTag.__typename === 'PatientName' &&
          patientOrTag.programInstanceId,
      )
      .filter(Boolean),
  );

  const watchedProgramTagIds = new Set(
    watchPatientsOrTags
      .map(
        (patientOrTag) =>
          patientOrTag.__typename === 'ProgramTag' && patientOrTag.id,
      )
      .filter(Boolean),
  );

  const combinedPatientsAndTagsList = [...providerProgramTags, ...patientsList];

  const filteredPatientsOrTags = combinedPatientsAndTagsList
    .filter((patientOrTag) => {
      if (patientOrTag.__typename === 'PatientName') {
        return !watchedProgramInstanceIds.has(patientOrTag.programInstanceId);
      } else if (patientOrTag.__typename === 'ProgramTag') {
        return !watchedProgramTagIds.has(patientOrTag.id);
      }
      return true;
    })
    .filter((patientOrTag) => {
      if (patientOrTagQuery) {
        if (patientOrTag.__typename === 'PatientName') {
          return (
            patientOrTag?.firstName
              ?.toLowerCase()
              .includes(patientOrTagQuery.toLowerCase()) ||
            patientOrTag?.lastName
              ?.toLowerCase()
              .includes(patientOrTagQuery.toLowerCase())
          );
        } else if (patientOrTag.__typename === 'ProgramTag') {
          return patientOrTag?.name
            ?.toLowerCase()
            .includes(patientOrTagQuery.toLowerCase());
        }
      }
      return true;
    });

  const recipientsAreEqual = (a: any, b: any) => {
    if (isPatientName(a) && isPatientName(b)) {
      return a.programInstanceId === b.programInstanceId;
    }
    if (isProgramTag(a) && isProgramTag(b)) {
      return a.id === b.id;
    }
    return false;
  };

  const setRecipients = async (nextSelection: any[]) => {
    const currentValue = buildRecipientsValue(
      tagSelections,
      tagOrder,
      patientSelections,
      patientOrder,
    );

    const addedItems = nextSelection.filter(
      (item) =>
        !currentValue.some((existing) => recipientsAreEqual(existing, item)),
    );
    const removedItems = currentValue.filter(
      (item) =>
        !nextSelection.some((existing) => recipientsAreEqual(existing, item)),
    );

    const nextState = cloneSelectionState();

    removedItems.forEach((item) => {
      if (isProgramTag(item)) {
        removeTagFromState(nextState, item.id);
      } else if (isPatientName(item)) {
        removePatientFromState(nextState, item.programInstanceId);
      }
    });

    for (const item of addedItems) {
      if (isProgramTag(item)) {
        if (nextState.tagSelections[item.id]) {
          continue;
        }

        try {
          const { data } = await patientNamesForRecipientsQuery({
            variables: { programIds: [], programTagIds: [item.id] },
          });
          const fetchedPatients = sortAndNormalizePatients(
            data?.patientNamesForRecipients as
              | PatientOrPatientNameData[]
              | undefined,
          );
          addTagToState(
            nextState,
            item as ProgramTagWithCountDataFragment,
            fetchedPatients,
          );
        } catch (err) {
          customToast.error('Unable to load tag recipients.');
        }
      } else if (isPatientName(item)) {
        addPatientToState(nextState, item, { manual: true });
      }
    }

    setTagSelections(nextState.tagSelections);
    setTagOrder(nextState.tagOrder);
    setPatientSelections(nextState.patientSelections);
    setPatientOrder(nextState.patientOrder);

    return buildRecipientsValue(
      nextState.tagSelections,
      nextState.tagOrder,
      nextState.patientSelections,
      nextState.patientOrder,
    );
  };

  return {
    tagSelections,
    setTagSelections,
    tagOrder,
    setTagOrder,
    patientSelections,
    setPatientSelections,
    patientOrder,
    setPatientOrder,
    patientOrTagQuery,
    setPatientOrTagQuery,
    filteredPatientsOrTags,
    cloneSelectionState,
    addPatientToState,
    addTagToState,
    removePatientFromState,
    removeTagFromState,
    buildRecipientsValue,
    sortAndNormalizePatients,
    setRecipients,
  };
};
